import UnAuthenticatedRoutesArr from '../UnAuthenticatedRoutesArr';
import CONSTANTS from '../Constants';

const isModernNavigationEnabled = () =>
    sessionStorage.getItem('modernNavigationEnabled') === 'true';

const clearBrandingVars = () => {
    sessionStorage.removeItem('modernBrandingJSON');
    sessionStorage.removeItem('modernLogos');
    const { dbk } = window;

    if (dbk?.setModernBranding) {
        // eslint-disable-next-line no-null/no-null
        dbk.setModernBranding(null);
    }

    if (window.legacyBrandingJsonData) {
        Object.keys(window.legacyBrandingJsonData).forEach((key) => {
            document.documentElement.style.removeProperty(key);
        });
        window.legacyBrandingJsonData = {};
    }
};

const isUnauthenticatedPath = (
    pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => UnAuthenticatedRoutesArr.some((path) => pathname.includes(path));

/**
 * True when modern navigation chrome (accent/full CSS, inverse logos) should
 * apply. Unauthenticated routes still load `modernBranding.css` via App.js
 * `showModernCSS` for login theming, but not navigation/logo changes.
 */
const isModernBrandingCssActive = (
    pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
    if (isUnauthenticatedPath(pathname)) {
        return false;
    }

    const isModernEnrolled =
        sessionStorage.getItem('modernEnrollmentOption') === 'AUTO_ENROLL';
    const modernCSSEnabled =
        sessionStorage.getItem('modernCSSenabled') === 'true';
    const hasModernBranding = !!sessionStorage.getItem('modernBrandingJSON');

    return (
        isModernEnrolled &&
        modernCSSEnabled &&
        isModernNavigationEnabled() &&
        hasModernBranding
    );
};

/**
 * Branding payloads may nest navigation under `Navigation` or `navigation`.
 * Keep stylesheet / inverse-logo readers aligned with setBrandingVars.
 */
const getNavigationConfig = (components = {}) =>
    components?.Navigation ?? components?.navigation;

/**
 * Safely read modern logos from session storage. Missing keys, the string
 * "undefined" (from setItem + JSON.stringify(undefined)), and other invalid
 * JSON must not throw — LogoSection / Header parse this on every render.
 */
const getModernLogos = () => {
    try {
        const stored = sessionStorage.getItem('modernLogos');

        if (!stored) {
            return undefined;
        }

        const parsed = JSON.parse(stored);

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return undefined;
        }

        return parsed;
    } catch {
        return undefined;
    }
};

/**
 * Safely read modern branding JSON from session storage. Missing keys, the
 * string "undefined", and other invalid JSON must not throw —
 * isInverseLogoEnabled is called from LogoSection / Header on every render.
 */
const getModernBrandingJSON = () => {
    try {
        const stored = sessionStorage.getItem('modernBrandingJSON');

        if (!stored) {
            return undefined;
        }

        const parsed = JSON.parse(stored);

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return undefined;
        }

        return parsed;
    } catch {
        return undefined;
    }
};

/**
 * Inverse logos are for full-color navigation only, and only while modern
 * branding CSS is actually active for the current user/path.
 * Non-string `style` values must not throw — optional chaining only guards
 * null/undefined, and LogoSection / Header call this on every render.
 */
const isInverseLogoEnabled = (pathname) => {
    // Inverse logos are part of full-color nav; never on login/register/MFA.
    if (!isModernBrandingCssActive(pathname)) {
        return false;
    }

    const modernBrandingJSON = getModernBrandingJSON();
    const navigationStyle = getNavigationConfig(modernBrandingJSON?.components)
        ?.style;

    // Match App.js: only call string methods when style is actually a string
    // (e.g. `?.toUpperCase()` still throws if style is a number/object).
    return (
        typeof navigationStyle === 'string' &&
        navigationStyle.toUpperCase() === CONSTANTS.NavStyles.FULL
    );
};

/**
 * Pre-modern leapfrog stylesheet fallback when nxg-ui `default-brandings?type=legacy`
 * (or `brandings?type=legacy`) returns no theme (e.g. 204). Not the authenticated
 * /brandings response still held in the store or session.
 */
const applyLeapfrogStylesheetFallback = (leapfrogBrandingFile) => {
    if (
        !leapfrogBrandingFile ||
        document.getElementById('leapfrogBrandingStyle') ||
        document.getElementById('experience-branding-css') ||
        window.parent !== window
    ) {
        return;
    }

    const link = document.createElement('link');
    link.href = leapfrogBrandingFile;
    link.rel = 'stylesheet';
    link.id = 'leapfrogBrandingStyle';
    document.body.appendChild(link);
};

/**
 * After `default-brandings?type=legacy` has no theme: clear modern tokens and load leapfrog CSS.
 */
const applyLegacyBrandingFallback = (appConfig) => {
    clearBrandingVars();
    applyLeapfrogStylesheetFallback(appConfig?.leapfrogBrandingFile);
};

const setBrandingVars = (branding) => {
    // use use: if the FI is not migrated to modern experience and modernCSSEnabled is enabled, remove the modern branding JSON from session storage to avoid the old branding from being applied
    if (!branding || Object.keys(branding).length === 0) {
        clearBrandingVars();

        return;
    }

    const myVariables = {};
    const { dbk } = window;
    const { palette = {}, typography = {}, components = {} } = branding;

    Object.keys(palette).forEach((key) => {
        // eslint-disable-next-line no-null/no-null
        if (typeof palette[key] === 'object' && palette[key] !== null) {
            Object.keys(palette[key]).forEach((value) => {
                myVariables[`--${key}-${value}`] = palette[key][value];
            });
        } else {
            myVariables[`--${key}`] = palette[key];
        }
    });

    Object.keys(typography).forEach((key) => {
        // eslint-disable-next-line no-null/no-null
        if (typeof typography[key] === 'object' && typography[key] !== null) {
            Object.keys(typography[key]).forEach((value) => {
                if (
                    value === 'fontSize' &&
                    typeof typography[key][value] === 'number'
                ) {
                    myVariables[
                        `--${key}-${value}`
                    ] = `${typography[key][value]}px`;
                } else {
                    myVariables[`--${key}-${value}`] = typography[key][value];
                }
            });
        } else {
            myVariables[`--${key}`] = typography[key];
        }
    });

    // Resolves a logical color choice ("Primary"/"Secondary") into the actual
    // palette group (main/dark/light/contrastText/etc.) it points to.
    const resolvePaletteColor = (colorChoice) => {
        if (!colorChoice) {
            return undefined;
        }

        const colorName =
            colorChoice.toLowerCase() === 'secondary' ? 'secondary' : 'primary';

        return palette[colorName];
    };

    const buttonColorChoice =
        components?.MuiButton?.defaultProps?.color ??
        components?.button?.bgcolor;
    const buttonPalette = resolvePaletteColor(buttonColorChoice);

    if (buttonPalette) {
        Object.keys(buttonPalette).forEach((value) => {
            myVariables[`--buttonPrimary-${value}`] = buttonPalette[value];
        });
    }

    // The `.../brandings?type=legacy` response nests navigation config under
    // `themes.light.components.Navigation`, e.g.
    // `{ "backgroundColor": "primary", "style": "Accent" }`. `backgroundColor`
    // is a logical pointer ("primary"/"secondary") into the palette, resolved
    // below. Fallback themes (e.g. defaultModernBranding) often omit
    // `Navigation` entirely, but `modernBranding.css` still references
    // `--navigation-*` for quick links and related nav chrome — always resolve
    // to the "primary" palette group when config or backgroundColor is absent.
    if (isModernNavigationEnabled()) {
        const navigationConfig = getNavigationConfig(components);
        const navColorChoice =
            navigationConfig?.backgroundColor ?? navigationConfig?.bgcolor;
        const navPalette =
            resolvePaletteColor(navColorChoice) ?? palette.primary;

        if (navPalette) {
            if (navPalette.main) {
                myVariables['--navigation-main'] = navPalette.main;
            }

            if (navPalette.contrastText) {
                myVariables['--navigation-contrastText'] =
                    navPalette.contrastText;
            }
        }
    }

    const setVariables = (vars) =>
        Object.entries(vars).forEach((v) =>
            document.documentElement.style.setProperty(v[0], v[1]),
        );
    window.legacyBrandingJsonData = myVariables; // legacyBrandingJsonData is for CSS global variables
    setVariables(myVariables);

    // Set Modern Branding to session and window objects
    sessionStorage.setItem('modernBrandingJSON', JSON.stringify(branding));

    if (dbk) {
        dbk.setModernBranding(branding);
    }
};

export {
    applyLegacyBrandingFallback,
    applyLeapfrogStylesheetFallback,
    clearBrandingVars,
    getModernBrandingJSON,
    getModernLogos,
    getNavigationConfig,
    isInverseLogoEnabled,
    isModernBrandingCssActive,
    isModernNavigationEnabled,
    isUnauthenticatedPath,
    setBrandingVars,
};
